import React from 'react';
import { Box, Typography } from '@mui/material';
import { error, warning, success, info, primary, neutral } from '../../_foundations/phxColors';

export type PhxBadgeColor = 'error' | 'warning' | 'success' | 'info' | 'primary' | 'secondary' | 'default' | 'neutral';
export type PhxBadgeSize = 'sm' | 'md';

export interface PhxBadgeProps {
  /**
   * The content of the badge
   */
  children: React.ReactNode;
  /**
   * The color of the badge
   * @default 'default'
   */
  color?: PhxBadgeColor;
  /**
   * The size of the badge
   * @default 'md'
   */
  size?: PhxBadgeSize;
  /**
   * Element placed before the children
   */
  startIcon?: React.ReactNode;
  /**
   * Element placed after the children
   */
  endIcon?: React.ReactNode;
}

const getColorStyles = (color: PhxBadgeColor) => {
  const colorMap: Record<PhxBadgeColor, { bg: string; text: string; border: string }> = {
    error: { bg: error[50], text: error[700], border: error[50] },
    warning: { bg: warning[50], text: warning[700], border: warning[50] },
    success: { bg: success[50], text: success[700], border: success[50] },
    info: { bg: info[50], text: info[700], border: info[50] },
    primary: { bg: primary[50], text: primary[700], border: primary[50] },
    secondary: { bg: 'transparent', text: neutral[700], border: primary[700] },
    default: { bg: neutral[50], text: neutral[700], border: neutral[50] },
    neutral: { bg: 'transparent', text: neutral[700], border: neutral[200] },
  };

  return colorMap[color];
};

export const PhxBadge: React.FC<PhxBadgeProps> = ({ children, color = 'default', size = 'md', startIcon, endIcon }) => {
  const colorStyles = getColorStyles(color);

  return (
    <Box
      className='lex-badge'
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        padding: size === 'sm' ? '2px 8px' : '4px 8px',
        height: '22px',
        borderRadius: '16px',
        backgroundColor: colorStyles.bg,
        border: '1px solid',
        borderColor: colorStyles.border,
        flexShrink: 0,
      }}
    >
      {startIcon && (
        <Box
          component='span'
          sx={{
            display: 'flex',
            alignItems: 'center',
            '& > svg': {
              fontSize: size === 'sm' ? 14 : 16,
              color: colorStyles.text,
            },
          }}
        >
          {startIcon}
        </Box>
      )}
      <Typography
        variant={size === 'sm' ? 'captionMd' : 'bodyMd'}
        sx={{
          color: colorStyles.text,
          fontWeight: 500,
        }}
      >
        {children}
      </Typography>
      {endIcon && (
        <Box
          component='span'
          sx={{
            display: 'flex',
            alignItems: 'center',
            '& > svg': {
              fontSize: size === 'sm' ? 14 : 16,
              color: colorStyles.text,
            },
          }}
        >
          {endIcon}
        </Box>
      )}
    </Box>
  );
};

export default PhxBadge;
